class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.addEventListener('click', (event) => {
      if (this.classList.contains('active') && !event.target.closest('.drawer__inner')) this.close();
    });
    this.addEventListener('click', (event) => {
      if (this.dataset.zenaroUpdating !== 'true') return;
      if (!event.target.closest('#CartDrawer-Checkout, #rsi_buy_now_button, .releasit-cod-form-cart-button, quantity-input button, cart-remove-button')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);
  }

  connectedCallback() {
    this.setHeaderCartIconAccessibility();
    // The full-page reload already supplies fresh Liquid. A second initial
    // render can overwrite a newer cart and the app's mounted button.
    this.viewportController?.abort();
    this.viewportController = new AbortController();
    const options = { passive: true, signal: this.viewportController.signal };
    const update = () => this.updateViewport();
    window.addEventListener('resize', update, options);
    window.addEventListener('pageshow', update, options);
    window.visualViewport?.addEventListener('resize', update, options);
    window.visualViewport?.addEventListener('scroll', update, options);
    this.updateViewport();
  }

  disconnectedCallback() {
    this.visibilityRevision = (this.visibilityRevision || 0) + 1;
    this.viewportController?.abort();
    this.headerController?.abort();
  }

  updateViewport() {
    const viewport = window.visualViewport;
    const height = Math.round(viewport?.height || window.innerHeight);
    const top = Math.round(viewport?.offsetTop || 0);
    this.style.setProperty('--zenaro-cart-viewport-height', `${height}px`);
    this.style.setProperty('--zenaro-cart-viewport-top', `${top}px`);
  }

  preserveReleasit() {
    const nativeButton = this.querySelector('#rsi_buy_now_button, .releasit-cod-form-cart-button');
    const wrapper = nativeButton && (nativeButton.closest('#cart-wrapper') || nativeButton.parentElement);
    if (wrapper) window.__zenaroReleasitNodeCache = { nativeButton, wrapper };
  }

  restoreReleasit() {
    const checkout = this.querySelector('#CartDrawer-Checkout');
    const cached = window.__zenaroReleasitNodeCache;
    if (!checkout || checkout.disabled || !cached?.wrapper) return;
    if (this.querySelector('#rsi_buy_now_button, .releasit-cod-form-cart-button')) return;
    (checkout.closest('.cart__ctas') || checkout).insertAdjacentElement('afterend', cached.wrapper);
  }

  setHeaderCartIconAccessibility() {
    this.headerController?.abort();
    this.headerController = new AbortController();
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    const options = { signal: this.headerController.signal };
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      window.__zenaroRequestCartOpen ? window.__zenaroRequestCartOpen() : this.open(cartLink);
    }, options);
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        window.__zenaroRequestCartOpen ? window.__zenaroRequestCartOpen() : this.open(cartLink);
      }
    }, options);
  }

  open(triggeredBy) {
    // Zenaro loader-before-drawer-open fix 2026-08-28
    if (window.__zenaroCartReloadPending === true) return;
    const visibilityRevision = this.visibilityRevision = (this.visibilityRevision || 0) + 1;
    this.updateViewport();

    this.style.removeProperty('visibility');
    this.style.removeProperty('pointer-events');
    this.removeAttribute('aria-hidden');

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.removeAttribute('aria-hidden');
      overlay.style.removeProperty('display');
      overlay.style.removeProperty('opacity');
      overlay.style.removeProperty('visibility');
      overlay.style.removeProperty('pointer-events');
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      if (this.visibilityRevision === visibilityRevision) this.classList.add('animate', 'active');
    });

    this.addEventListener('transitionend', () => {
      if (this.visibilityRevision !== visibilityRevision || !this.classList.contains('active')) return;
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    const visibilityRevision = this.visibilityRevision = (this.visibilityRevision || 0) + 1;
    const returnFocus = this.activeElement?.isConnected && !this.contains(this.activeElement)
      ? this.activeElement
      : document.getElementById('cart-icon-bubble');
    try {
      removeTrapFocus(returnFocus);
    } catch (error) {}
    if (this.contains(document.activeElement)) document.activeElement.blur();
    // Zenaro mobile overlay close fix 2026-08-28
    this.classList.remove('active', 'animate');
    this.setAttribute('aria-hidden', 'true');
    this.style.setProperty('visibility', 'hidden');
    this.style.setProperty('pointer-events', 'none');

    const forceClosedState = () => {
      if (this.visibilityRevision !== visibilityRevision || this.classList.contains('active') || window.__zenaroCartReloadPending === true) return;

      this.setAttribute('aria-hidden', 'true');
      this.style.setProperty('visibility', 'hidden');
      this.style.setProperty('pointer-events', 'none');

      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.setAttribute('aria-hidden', 'true');
        overlay.style.setProperty('display', 'none');
        overlay.style.setProperty('opacity', '0');
        overlay.style.setProperty('visibility', 'hidden');
        overlay.style.setProperty('pointer-events', 'none');
      }

      document.body.classList.remove('overflow-hidden');
      document.documentElement.classList.remove(
        'zenaro-cart-reloading-for-releasit',
        'cabecera-kylie-carrito-abierto'
      );

      // Zenaro definitive closed-cart backdrop guard 2026-08-28
      if (!document.body.classList.contains('cabecera-kylie-menu-abierto')) {
        document.querySelectorAll('.cabecera-kylie-overlay').forEach((headerOverlay) => {
          headerOverlay.classList.remove('cabecera-kylie-overlay--visible');
          headerOverlay.setAttribute('aria-hidden', 'true');
          headerOverlay.style.setProperty('opacity', '0');
          headerOverlay.style.setProperty('visibility', 'hidden');
          headerOverlay.style.setProperty('pointer-events', 'none');
        });
      }

      const reloadLoader = document.getElementById('zenaro-cart-refresh-loader');
      if (reloadLoader) {
        reloadLoader.classList.remove('is-visible');
        reloadLoader.style.setProperty('pointer-events', 'none');
      }
    };

    forceClosedState();

    requestAnimationFrame(forceClosedState);
    setTimeout(forceClosedState, 350);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  refreshFromServer() {
    window.dispatchEvent(new CustomEvent('zenaro:cart-resync'));
  }

  renderContents(parsedState) {
    if (window.__zenaroDeferredCart && !this.classList.contains("active")) return;
    const updates = this.getSectionsToRender().map((section) => {
      const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const html = parsedState?.sections?.[section.id];
      const content = typeof html === 'string' ? this.getSectionDOM(html, section.selector) : null;
      return { target, content };
    });
    if (updates.some(({ target, content }) => !target || !content)) {
      this.refreshFromServer();
      return false;
    }
    const releasitNativeButton = this.querySelector('#rsi_buy_now_button, .releasit-cod-form-cart-button');
    const releasitWrapper = releasitNativeButton &&
      (releasitNativeButton.closest('#cart-wrapper') || releasitNativeButton.parentElement);

    if (releasitWrapper) {
      window.__zenaroReleasitNodeCache = {
        nativeButton: releasitNativeButton,
        wrapper: releasitWrapper
      };
      releasitWrapper.remove();
    }

    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    updates.forEach(({ target, content }) => {
      target.innerHTML = content.innerHTML;
    });

    if (releasitWrapper) {
      const checkoutButton = this.querySelector('#CartDrawer-Checkout');
      const checkoutRow = checkoutButton && (checkoutButton.closest('.cart__ctas') || checkoutButton);

      if (checkoutButton && !checkoutButton.disabled && checkoutRow) {
        checkoutRow.insertAdjacentElement('afterend', releasitWrapper);
      }
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      if (this.classList.contains("active")) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Zenaro cart-only visual refinement
(() => {
  const STYLE_ID = 'zenaro-cart-only-style';
  const RELEASIT_NATIVE_SELECTOR = '#rsi_buy_now_button, .releasit-cod-form-cart-button';

  const installStyles = () => {
    let style = document.getElementById(STYLE_ID);
    if (style) return;
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      document.head.appendChild(style);
    }

    style.textContent = `
      cart-drawer {
        --accent-color: 150, 0, 231;
        --color-base-accent-1: 150, 0, 231;
        --color-button: 150, 0, 231;
      }

      cart-drawer .drawer__heading,
      cart-drawer .cart-item__name,
      cart-drawer .cart-drawer__final-item-price,
      cart-drawer .totals__total-value,
      cart-drawer .totals__total,
      cart-drawer .quantity__button,
      cart-drawer .quantity__input,
      cart-drawer .cart-drawer__total-estimado,
      cart-drawer .cart-drawer__iva-incluido {
        color: #9600e7 !important;
      }

      cart-drawer .drawer__heading {
        font-weight: 500;
        margin: 0 !important;
      }

      cart-drawer .drawer__header {
        position: relative;
        padding: 1.8rem 0 1.45rem !important;
        margin-bottom: 1rem !important;
      }

      cart-drawer .zenaro-cart-urgency {
        display: none !important;
      }

      cart-drawer .cart-progress {
        display: none !important;
      }

      cart-drawer .quantity,
      cart-drawer .quantity::before,
      cart-drawer .quantity::after {
        border-color: #9600e7 !important;
        box-shadow: none !important;
      }

      cart-drawer .cart-drawer__total-estimado {
        margin-top: .8rem !important;
        margin-bottom: 0 !important;
      }

      cart-drawer .cart-drawer__iva-incluido {
        margin-top: .05rem !important;
        margin-bottom: .55rem !important;
      }

      cart-drawer .cart__ctas {
        margin-top: .55rem !important;
      }

      cart-drawer .zenaro-cod-ctas {
        margin-top: .45rem !important;
      }

      cart-drawer .cart__checkout-button,
      cart-drawer .cart__checkout-button.button {
        background: #9600e7 !important;
        color: #fff !important;
      }

      cart-drawer .zenaro-cart-cod-button,
      cart-drawer .zenaro-cart-cod-button.button {
        width: 100% !important;
        min-width: 0 !important;
        box-sizing: border-box !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        text-align: center !important;
        padding: 0 1.2rem !important;
        background: #fff !important;
        color: #9600e7 !important;
        border: 1px solid #9600e7 !important;
        box-shadow: none !important;
        transform: none !important;
        animation: none !important;
        white-space: nowrap !important;
        line-height: 1 !important;
        letter-spacing: .04em !important;
        transition: background-color .18s ease !important;
      }

      cart-drawer .zenaro-cart-cod-button::before,
      cart-drawer .zenaro-cart-cod-button::after,
      cart-drawer .zenaro-cart-cod-button span::before,
      cart-drawer .zenaro-cart-cod-button span::after {
        content: none !important;
        display: none !important;
        box-shadow: none !important;
      }

      cart-drawer .zenaro-cart-cod-button .icon-arrow,
      cart-drawer .zenaro-cart-cod-button svg {
        display: none !important;
      }

      cart-drawer .zenaro-cart-cod-button *,
      cart-drawer .zenaro-cart-cod-button:hover *,
      cart-drawer .zenaro-cart-cod-button:focus * {
        transform: none !important;
        animation: none !important;
        transition: none !important;
        white-space: nowrap !important;
        line-height: 1 !important;
        letter-spacing: .04em !important;
        text-align: center !important;
      }

      cart-drawer .zenaro-cart-cod-button:hover,
      cart-drawer .zenaro-cart-cod-button:focus {
        width: 100% !important;
        min-width: 0 !important;
        box-sizing: border-box !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        text-align: center !important;
        padding: 0 1.2rem !important;
        background: #fff7fb !important;
        color: #9600e7 !important;
        border-color: #9600e7 !important;
        transform: none !important;
        animation: none !important;
        white-space: nowrap !important;
        line-height: 1 !important;
        letter-spacing: .04em !important;
      }

      cart-drawer .zenaro-cart-cod-row {
        align-items: stretch !important;
      }

      cart-drawer .zenaro-cart-cod-row > .zenaro-cart-cod-button-slot {
        order: 1 !important;
      }

      cart-drawer .zenaro-cart-cod-row > .zenaro-cart-cod-note {
        order: 2 !important;
      }

      cart-drawer .zenaro-cart-cod-note {
        margin: 0 !important;
        padding: .15rem .25rem;
        min-width: 0 !important;
        width: 100% !important;
        max-width: 100% !important;
        min-height: 100%;
        box-sizing: border-box !important;
        overflow-wrap: anywhere;
        word-break: normal;
        display: flex;
        align-items: center;
        justify-content: center;
        color: rgba(43, 43, 43, .60);
        font-size: 1.05rem;
        line-height: 1.35;
        text-align: center;
      }

      cart-drawer .cart__badges {
        margin-top: .7rem !important;
        margin-bottom: 0 !important;
      }

      cart-drawer .zenaro-payment-trust {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: .5rem;
        margin-top: .55rem;
        color: rgba(43, 43, 43, .62);
        font-size: .98rem;
        line-height: 1.2;
        text-align: center;
      }

      cart-drawer .zenaro-payment-trust svg {
        width: 1.25rem;
        height: 1.25rem;
        flex: 0 0 auto;
        stroke: #9600e7;
      }

      @media screen and (min-width: 750px) {
        cart-drawer .zenaro-cart-cod-row {
          display: grid !important;
          grid-template-columns: minmax(14rem, .9fr) minmax(0, 1.35fr);
          gap: 1.2rem;
          align-items: stretch !important;
        }

        cart-drawer .zenaro-cart-cod-button,
        cart-drawer .zenaro-cart-cod-button.button {
          align-self: stretch !important;
          font-size: 1.05rem !important;
          letter-spacing: .03em !important;
          padding-left: .65rem !important;
          padding-right: .65rem !important;
          text-align: center !important;
          justify-content: center !important;
        }

        cart-drawer .zenaro-cart-cod-row > .zenaro-cart-cod-button-slot {
          grid-column: 1 !important;
        }

        cart-drawer .zenaro-cart-cod-note {
          grid-column: 2 !important;
          align-self: stretch !important;
        }
      }

      @media screen and (max-width: 749px) {
        cart-drawer .drawer__header {
          padding: 1.7rem 0 1.35rem !important;
          margin-bottom: .9rem !important;
        }

        cart-drawer .zenaro-cart-cod-row {
          display: flex !important;
          flex-direction: column !important;
          align-items: stretch !important;
          gap: 1rem !important;
        }

        cart-drawer .zenaro-cart-cod-note {
          margin: 0 !important;
          padding: 0 .8rem !important;
        }

        cart-drawer .zenaro-payment-trust {
          margin-top: .45rem;
          font-size: .92rem;
        }
      }
    `;

    style.textContent += `
      cart-drawer .zenaro-cart-accessory-upsells__form .product-form__error-message-wrapper {
        display: none !important;
      }

      cart-drawer #cart-wrapper.zenaro-cart-cod-row {
        width: 100% !important;
        min-width: 0 !important;
        max-width: 100% !important;
        margin: .8rem 0 0 !important;
        box-sizing: border-box !important;
      }

      cart-drawer #rsi_buy_now_button.zenaro-cart-cod-button,
      cart-drawer #rsi_buy_now_button.zenaro-cart-cod-button:hover,
      cart-drawer #rsi_buy_now_button.zenaro-cart-cod-button:focus {
        width: 100% !important;
        min-width: 0 !important;
        height: 46px !important;
        min-height: 46px !important;
        margin: 0 !important;
        padding: 0 18px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        box-sizing: border-box !important;
        background: #fff !important;
        color: #9600e7 !important;
        border: 1px solid #9600e7 !important;
        border-radius: 6px !important;
        box-shadow: none !important;
        transform: none !important;
        animation: none !important;
        font-size: 1.05rem !important;
        line-height: 1 !important;
        font-weight: 600 !important;
        letter-spacing: .03em !important;
        text-transform: uppercase !important;
        text-align: center !important;
        cursor: pointer !important;
        overflow: hidden !important;
        white-space: nowrap !important;
      }

      cart-drawer #rsi_buy_now_button.zenaro-cart-cod-button:hover,
      cart-drawer #rsi_buy_now_button.zenaro-cart-cod-button:focus {
        background: #fff7fb !important;
      }

      cart-drawer #rsi_buy_now_button.zenaro-cart-cod-button > div,
      cart-drawer #rsi_buy_now_button.zenaro-cart-cod-button > div > div {
        width: 100% !important;
        min-width: 0 !important;
        max-width: 100% !important;
        height: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        box-sizing: border-box !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        overflow: hidden !important;
        white-space: nowrap !important;
        line-height: 1 !important;
        color: inherit !important;
        text-align: center !important;
        visibility: visible !important;
      }

      @media screen and (max-width: 749px) {
        cart-drawer #rsi_buy_now_button.zenaro-cart-cod-button {
          height: 40px !important;
          min-height: 40px !important;
          padding: 0 14px !important;
          font-size: 1.05rem !important;
          letter-spacing: .03em !important;
        }
      }
    `;

    style.textContent += `
      /* Empty cart: restore comfortable desktop spacing around the upsell only. */
      @media screen and (min-width: 750px) {
        cart-drawer.is-empty .cart-drawer-items-and-upsell {
          width: 100% !important;
          min-width: 0 !important;
          box-sizing: border-box !important;
          padding: 0 2.4rem 2.2rem !important;
        }

        cart-drawer.is-empty .cart-drawer-items-and-upsell > .zenaro-cart-accessory-upsells {
          width: 100% !important;
          min-width: 0 !important;
          margin: 0 !important;
          box-sizing: border-box !important;
        }
      }
    `;

    style.textContent += `
      @media screen and (max-width: 749px) {
        cart-drawer .cart-drawer-items-and-upsell {
          display: flex !important;
          flex-direction: column !important;
          overflow-y: auto !important;
          overscroll-behavior: contain;
        }

        cart-drawer .cart-drawer-items-and-upsell > .zenaro-cart-accessory-upsells {
          order: 1 !important;
          flex: 0 0 auto !important;
          position: relative;
          z-index: 3;
          margin: 0 !important;
          padding: .75rem 0 1rem !important;
          background: rgb(var(--color-background));
          border-top: 0 !important;
          border-bottom: .1rem solid rgba(var(--color-foreground), .10);
        }

        cart-drawer .cart-drawer-items-and-upsell > cart-drawer-items,
        cart-drawer .cart-drawer-items-and-upsell > .cart-drawer__form {
          order: 0;
          flex: 0 0 auto;
        }

        cart-drawer .zenaro-cart-accessory-upsells__header {
          margin-bottom: .65rem !important;
        }

        cart-drawer .zenaro-cart-accessory-upsells__hint {
          display: none !important;
        }

        cart-drawer .zenaro-cart-accessory-upsells__subtitle {
          display: none !important;
        }

        cart-drawer .zenaro-cart-accessory-upsells__title {
          font-size: 1.15rem !important;
          line-height: 1.15 !important;
        }

        cart-drawer .zenaro-cart-accessory-upsells__header {
          align-items: center !important;
          gap: .45rem !important;
        }

        cart-drawer .zenaro-cart-accessory-upsells__controls {
          gap: .2rem !important;
        }

        cart-drawer .zenaro-cart-accessory-upsells__arrow {
          width: 2.45rem !important;
          height: 2.45rem !important;
        }

        cart-drawer .zenaro-cart-accessory-upsells__arrow span {
          font-size: 1.75rem !important;
        }

        cart-drawer .zenaro-cart-accessory-upsells__counter {
          min-width: 2.8rem !important;
          font-size: .95rem !important;
        }

        cart-drawer .zenaro-cart-accessory-upsells__item {
          grid-template-columns: 4.25rem minmax(0, 1fr) auto !important;
          gap: .55rem !important;
          padding: .45rem !important;
          border-radius: .75rem !important;
        }

        cart-drawer .zenaro-cart-accessory-upsells__image-link {
          width: 4.25rem !important;
          height: 4.25rem !important;
          border-radius: .6rem !important;
        }

        cart-drawer .zenaro-cart-accessory-upsells__product-title,
        cart-drawer .zenaro-cart-accessory-upsells__price {
          font-size: 1.02rem !important;
          line-height: 1.15 !important;
        }

        cart-drawer .zenaro-cart-accessory-upsells__info {
          gap: .15rem !important;
        }

        cart-drawer .zenaro-cart-accessory-upsells__form,
        cart-drawer .zenaro-cart-accessory-upsells__button {
          min-width: 6.9rem !important;
        }

        cart-drawer .zenaro-cart-accessory-upsells__variant-label {
          margin-bottom: .15rem !important;
          font-size: .88rem !important;
        }

        cart-drawer .zenaro-cart-accessory-upsells__variant-select {
          min-height: 2.45rem !important;
          margin-bottom: .25rem !important;
          padding: 0 .35rem !important;
          font-size: .92rem !important;
        }

        cart-drawer .zenaro-cart-accessory-upsells__button {
          min-height: 2.75rem !important;
          padding: 0 .55rem !important;
          font-size: .98rem !important;
          border-radius: .6rem !important;
        }
      }
    `;
  };

  const normalizeText = (value) => (value || '').replace(/\s+/g, ' ').trim().toLowerCase();

  const setupUpsellCarousel = (drawer) => {
    const track = drawer.querySelector('.zenaro-cart-accessory-upsells__items');
    if (!track) return;

    const cards = Array.from(track.querySelectorAll('.zenaro-cart-accessory-upsells__item'));
    const wrapper = track.closest('.zenaro-cart-accessory-upsells');
    const previousButton = wrapper && wrapper.querySelector('[data-zenaro-upsell-prev]');
    const nextButton = wrapper && wrapper.querySelector('[data-zenaro-upsell-next]');
    const counter = wrapper && wrapper.querySelector('[data-zenaro-upsell-counter]');

    const getCurrentIndex = () => {
      if (cards.length < 2) return 0;

      const firstOffset = cards[0].offsetLeft;
      let nearestIndex = 0;
      let nearestDistance = Infinity;

      cards.forEach((card, index) => {
        const cardOffset = card.offsetLeft - firstOffset;
        const distance = Math.abs(track.scrollLeft - cardOffset);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    };

    const updateControls = () => {
      const currentIndex = getCurrentIndex();
      const lastIndex = Math.max(cards.length - 1, 0);
      const counterText = `${currentIndex + 1} / ${cards.length}`;

      if (previousButton) previousButton.disabled = currentIndex <= 0;
      if (nextButton) nextButton.disabled = currentIndex >= lastIndex;
      if (counter && counter.textContent !== counterText) counter.textContent = counterText;
    };

    const scrollToIndex = (index) => {
      if (!cards[index]) return;
      const firstOffset = cards[0].offsetLeft;
      track.scrollTo({
        left: cards[index].offsetLeft - firstOffset,
        behavior: 'smooth'
      });
    };

    if (track.dataset.zenaroCarouselBound !== 'true') {
      track.dataset.zenaroCarouselBound = 'true';

      let scrollFrame = null;
      track.addEventListener('scroll', () => {
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(updateControls);
      }, { passive: true });

      if (previousButton) {
        previousButton.addEventListener('click', () => {
          scrollToIndex(Math.max(getCurrentIndex() - 1, 0));
        });
      }

      if (nextButton) {
        nextButton.addEventListener('click', () => {
          scrollToIndex(Math.min(getCurrentIndex() + 1, cards.length - 1));
        });
      }
    }

    updateControls();
  };

  const polishCart = () => {
    installStyles();

    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const heading = drawer.querySelector('.drawer__heading');
    if (heading && heading.textContent !== 'Tu carrito') heading.textContent = 'Tu carrito';

    drawer.querySelectorAll('.zenaro-cart-urgency').forEach((element) => element.remove());

    drawer.querySelectorAll('#Zenaro-COD-Checkout, .releasit-cod-form-custom-button').forEach((element) => {
      if (!element.matches(RELEASIT_NATIVE_SELECTOR)) element.remove();
    });

    const checkoutButton = drawer.querySelector('#CartDrawer-Checkout');
    let nativeButton = drawer.querySelector(RELEASIT_NATIVE_SELECTOR);
    let releasitWrapper = nativeButton && (nativeButton.closest('#cart-wrapper') || nativeButton.parentElement);

    if (nativeButton && releasitWrapper) {
      window.__zenaroReleasitNodeCache = {
        nativeButton,
        wrapper: releasitWrapper
      };
    } else {
      const cachedReleasit = window.__zenaroReleasitNodeCache;

      if (cachedReleasit && cachedReleasit.nativeButton && cachedReleasit.wrapper) {
        nativeButton = cachedReleasit.nativeButton;
        releasitWrapper = cachedReleasit.wrapper;
      }
    }

    if (checkoutButton && !checkoutButton.disabled && nativeButton && releasitWrapper) {
      const checkoutRow = checkoutButton.closest('.cart__ctas') || checkoutButton;

      if (releasitWrapper.previousElementSibling !== checkoutRow) {
        checkoutRow.insertAdjacentElement('afterend', releasitWrapper);
      }

      releasitWrapper.classList.add('zenaro-cart-cod-row');
      nativeButton.classList.add('zenaro-cart-cod-button');
      nativeButton.removeAttribute('aria-hidden');
      nativeButton.tabIndex = 0;
      nativeButton.setAttribute('type', 'button');
      const codLabel = drawer.dataset.zenaroCodLabel || 'Pay on delivery';
      const textNodes = document.createTreeWalker(nativeButton, NodeFilter.SHOW_TEXT);
      let textNode;
      let labelFound = false;
      while ((textNode = textNodes.nextNode())) {
        if (textNode.parentElement?.closest('svg, script, style')) continue;
        const currentLabel = textNode.nodeValue.trim().replace(/\s+/g, ' ');
        if (currentLabel !== codLabel && !/^(contra entrega|cash on delivery|pagar al recibir|pay on delivery)$/i.test(currentLabel)) continue;
        if (currentLabel !== codLabel) textNode.nodeValue = codLabel;
        labelFound = true;
      }
      if (labelFound && nativeButton.getAttribute('aria-label') !== codLabel) {
        nativeButton.setAttribute('aria-label', codLabel);
      }
      drawer.querySelector('[data-zenaro-cod-retry]')?.remove();

      const buttonSlot = nativeButton.parentElement === releasitWrapper
        ? nativeButton
        : Array.from(releasitWrapper.children).find((child) => child.contains(nativeButton));

      releasitWrapper.querySelectorAll('.zenaro-cart-cod-button-slot').forEach((candidate) => {
        if (candidate !== buttonSlot) candidate.classList.remove('zenaro-cart-cod-button-slot');
      });
      if (buttonSlot) buttonSlot.classList.add('zenaro-cart-cod-button-slot');

      let note = releasitWrapper.querySelector('.zenaro-cart-cod-note');
      if (!note) {
        note = document.createElement('div');
        note.className = 'zenaro-cart-cod-note';
        releasitWrapper.appendChild(note);
      }
      const noteText = 'El pago contra entrega tiene un suplemento de 4,95 €, que verás antes de confirmar tu pedido.';
      if (note.textContent !== noteText) note.textContent = noteText;
      if (note.parentElement === releasitWrapper && note !== releasitWrapper.lastElementChild) {
        releasitWrapper.appendChild(note);
      }

      drawer.querySelectorAll('.zenaro-cart-cod-note').forEach((candidate) => {
        if (candidate !== note) candidate.remove();
      });
    }

    setupUpsellCarousel(drawer);

    const badges = drawer.querySelector('.cart__badges');
    if (badges) {
      let trust = drawer.querySelector('.zenaro-payment-trust');
      if (!trust) {
        trust = document.createElement('div');
        trust.className = 'zenaro-payment-trust';
        trust.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="5" y="10" width="14" height="10" rx="2" stroke-width="1.7"/><path d="M8.5 10V7.5a3.5 3.5 0 0 1 7 0V10" stroke-width="1.7" stroke-linecap="round"/></svg><span>Pagos seguros y protegidos</span>';
        badges.insertAdjacentElement('afterend', trust);
      }
    }
  };

  let scheduled = false;
  const schedulePolish = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      polishCart();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedulePolish, { once: true });
  } else {
    schedulePolish();
  }

  const observer = new MutationObserver(schedulePolish);
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();

if (!customElements.get('zenaro-conditional-upsells')) {
  class ZenaroConditionalUpsells extends HTMLElement {
    get storagePrefix() {
      return 'zenaro_upsell_v3_' + this.dataset.offerGroup + '_' + this.dataset.cartKey + '_';
    }
    readState(key, fallback) {
      window.__zenaroUpsellMemory ||= {};
      if (key === 'dismissed') {
        if (window.__zenaroUpsellMemory.zenaro_upsell_session_dismissed === true) return true;
        try {
          if (sessionStorage.getItem('zenaro_upsell_session_dismissed') === 'true') return true;
        } catch (error) {}
        try {
          if (document.cookie.split('; ').some((entry) => entry === 'zenaro_upsell_session_dismissed=true')) return true;
        } catch (error) {}
      }
      const fullKey = this.storagePrefix + key;
      try {
        const value = sessionStorage.getItem(fullKey);
        if (value !== null) return JSON.parse(value);
      } catch (error) {}
      try {
        const cookieName = encodeURIComponent(fullKey) + '=';
        const cookieValue = document.cookie
          .split('; ')
          .find((entry) => entry.startsWith(cookieName));
        if (cookieValue) return JSON.parse(decodeURIComponent(cookieValue.slice(cookieName.length)));
      } catch (error) {}
      return window.__zenaroUpsellMemory[fullKey] ?? fallback;
    }
    writeState(key, value) {
      window.__zenaroUpsellMemory ||= {};
      if (key === 'dismissed' && value === true) {
        window.__zenaroUpsellMemory.zenaro_upsell_session_dismissed = true;
        try { sessionStorage.setItem('zenaro_upsell_session_dismissed', 'true'); } catch (error) {}
        try { document.cookie = 'zenaro_upsell_session_dismissed=true; path=/; SameSite=Lax'; } catch (error) {}
      }
      const fullKey = this.storagePrefix + key;
      window.__zenaroUpsellMemory[fullKey] = value;
      try { sessionStorage.setItem(fullKey, JSON.stringify(value)); } catch (error) {}
      try {
        document.cookie = encodeURIComponent(fullKey) + '=' + encodeURIComponent(JSON.stringify(value)) + '; path=/; SameSite=Lax';
      } catch (error) {}
    }
    get cards() { return Array.from(this.querySelectorAll('[data-zenaro-upsell-rule]')); }
    connectedCallback() {
      this.drawer = this.closest('cart-drawer');
      this.floatingObserver?.disconnect();
      this.floatingObserver = new ResizeObserver(() => this.syncFloatingSpace());
      this.floatingObserver.observe(this);
      if (this.bound) { this.syncFloatingSpace(); return; }
      this.bound = true;
      const cards = this.cards;
      cards.forEach(card => {
        const eyebrow = card.querySelector('.zenaro-conditional-upsell__eyebrow');
        const productPath = card.querySelector('.zenaro-conditional-upsell__image')?.getAttribute('href') || '';
        if (eyebrow) eyebrow.textContent = productPath.includes('/products/origen-botanico-kit-cuidado-post-keratina')
          ? 'Completa con el kit post queratina'
          : 'Complementa tu rutina';
        const select = card.querySelector('[data-zenaro-variant-select]');
        const form = select?.closest('form');
        const first = select && Array.from(select.options).find(option => !option.disabled && option.value);
        if (!select || !form || !first) return;
        card.dataset.zenaroOfferVariantId = first.value;
        const price = card.querySelector('.zenaro-conditional-upsell__price');
        if (price && first.dataset.price) price.textContent = first.dataset.price;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = select.name || 'id';
        input.value = first.value;
        Array.from(select.labels || []).forEach(label => label.remove());
        select.replaceWith(input);
      });
      let saved = this.readState('order', []);
      if (!Array.isArray(saved)) saved = [];
      const incoming = cards.map(card => card.dataset.zenaroUpsellRule).filter(id => !saved.includes(id));
      for (let i = incoming.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [incoming[i], incoming[j]] = [incoming[j], incoming[i]];
      }
      const order = [...saved, ...incoming];
      this.writeState('order', order);
      const priority = this.dataset.priorityRule;
      cards.sort((a, b) => {
        const aId = a.dataset.zenaroUpsellRule;
        const bId = b.dataset.zenaroUpsellRule;
        if (aId === priority) return -1;
        if (bId === priority) return 1;
        return order.indexOf(aId) - order.indexOf(bId);
      });
      const navigation = this.querySelector('[data-zenaro-offer-navigation]');
      cards.forEach(card => this.insertBefore(card, navigation));
      if (navigation) {
        navigation.replaceChildren(...cards.map((card, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.dataset.zenaroOfferIndex = index;
          dot.setAttribute('aria-label', card.querySelector('h3')?.textContent || String(index + 1));
          return dot;
        }));
      }
      let touchStart = null;
      this.addEventListener('touchstart', event => {
        if (event.target.closest('button, select, input')) return;
        const touch = event.touches[0];
        touchStart = { x: touch.clientX, y: touch.clientY };
      }, { passive: true });
      this.addEventListener('touchend', event => {
        if (!touchStart) return;
        const touch = event.changedTouches[0];
        const dx = touch.clientX - touchStart.x;
        const dy = touch.clientY - touchStart.y;
        touchStart = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        const currentCards = this.cards;
        if (currentCards.length < 2) return;
        this.activeIndex = (this.activeIndex + (dx < 0 ? 1 : -1) + currentCards.length) % currentCards.length;
        this.writeState('selected', currentCards[this.activeIndex].dataset.zenaroUpsellRule);
        this.showFirstEligibleRule();
      }, { passive: true });
      const selected = this.readState('selected', '');
      this.activeIndex = Math.max(0, cards.findIndex(card => card.dataset.zenaroUpsellRule === selected));
      this.addEventListener('change', event => {
        const select = event.target.closest('[data-zenaro-variant-select]');
        if (!select) return;
        const card = select.closest('[data-zenaro-upsell-rule]');
        card.dataset.zenaroOfferVariantId = select.value;
        card.querySelector('.zenaro-conditional-upsell__price').textContent = select.selectedOptions[0].dataset.price;
      });
      this.addEventListener('click', event => {
        const dot = event.target.closest('[data-zenaro-offer-index]');
        if (dot) {
          this.activeIndex = Number(dot.dataset.zenaroOfferIndex);
          this.writeState('selected', this.cards[this.activeIndex].dataset.zenaroUpsellRule);
          this.showFirstEligibleRule();
          return;
        }
        const step = event.target.closest('[data-zenaro-offer-step]');
        if (step) {
          const currentCards = this.cards;
          if (!currentCards.length) return;
          this.activeIndex = (this.activeIndex + Number(step.dataset.zenaroOfferStep) + currentCards.length) % currentCards.length;
          this.writeState('selected', currentCards[this.activeIndex].dataset.zenaroUpsellRule);
          this.showFirstEligibleRule();
          return;
        }
        if (event.target.closest('[data-zenaro-upsell-dismiss]')) {
          this.writeState('dismissed', true);
          this.showFirstEligibleRule();
        }
      });
      this.showFirstEligibleRule();
    }
    disconnectedCallback() {
      this.floatingObserver?.disconnect();
      this.drawer?.style.setProperty('--zenaro-upsell-space', '0px');
    }
    syncFloatingSpace() {
      if (!this.isConnected || !this.drawer) return;
      const space = this.hidden ? 0 : Math.ceil(this.getBoundingClientRect().height) + 24;
      const value = space + 'px';
      if (this.drawer.style.getPropertyValue('--zenaro-upsell-space') !== value) {
        this.drawer.style.setProperty('--zenaro-upsell-space', value);
      }
    }
    rememberNext(addedCard) {
      const cards = this.cards;
      const index = cards.indexOf(addedCard);
      const next = cards.length > 1 ? cards[(index + 1) % cards.length] : null;
      this.writeState('selected', next ? next.dataset.zenaroUpsellRule : '');
    }
    showFirstEligibleRule() {
      const dismissed = this.readState('dismissed', false) === true;
      const cards = this.cards;
      this.activeIndex = Math.min(this.activeIndex || 0, Math.max(0, cards.length - 1));
      cards.forEach((card, index) => { card.hidden = dismissed || index !== this.activeIndex; });
      this.hidden = dismissed || cards.length === 0;
      const navigation = this.querySelector('[data-zenaro-offer-navigation]');
      if (navigation) {
        navigation.hidden = dismissed || cards.length < 2;
        navigation.querySelectorAll('[data-zenaro-offer-index]').forEach((dot, index) => {
          dot.setAttribute('aria-current', index === this.activeIndex ? 'true' : 'false');
        });
      }
      this.classList.toggle('has-multiple-offers', cards.length > 1);
      this.syncFloatingSpace();
    }
  }
  customElements.define('zenaro-conditional-upsells', ZenaroConditionalUpsells);
}

// Reload after any successful Shopify cart add so Releasit initializes against the updated cart.
(() => {
  const REOPEN_KEY = 'zenaro_reopen_cart_after_add';
  const CHECKOUT_RETURN_KEY = 'zenaro_refresh_after_checkout';
  const RELOAD_CLASS = 'zenaro-cart-reloading-for-releasit';
  const STYLE_ID = 'zenaro-cart-reload-style';
  const LOADER_ID = 'zenaro-cart-refresh-loader';
  const FETCH_PATCH_FLAG = '__zenaroCartAddFetchPatched';
  const XHR_PATCH_FLAG = '__zenaroCartAddXhrPatched';
  const BOOTSTRAP_KEY = 'zenaro_releasit_initialized_for_cart';
  let reloadScheduled = false;
  let releasitAlreadyInitialized = false;

  try {
    const reloadPending = sessionStorage.getItem(REOPEN_KEY) === 'true';
    releasitAlreadyInitialized = sessionStorage.getItem(BOOTSTRAP_KEY) === 'true' || reloadPending;

    if (reloadPending) {
      sessionStorage.setItem(BOOTSTRAP_KEY, 'true');
    }
  } catch (error) {}

  const installReloadStyles = () => {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      html.${RELOAD_CLASS} cart-drawer,
      html.${RELOAD_CLASS} cart-notification,
      html.${RELOAD_CLASS} .cart-notification-wrapper,
      html.${RELOAD_CLASS} #CartDrawer,
      html.${RELOAD_CLASS} #CartDrawer-Overlay {
        opacity: 0 !important;
        visibility: hidden !important;
        pointer-events: none !important;
        transition: none !important;
      }

      html.${RELOAD_CLASS} body {
        cursor: wait !important;
      }

      #${LOADER_ID} {
        position: fixed;
        inset: 0;
        z-index: 2147483647;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2rem;
        background: rgba(255, 255, 255, .78);
        backdrop-filter: blur(2px);
        -webkit-backdrop-filter: blur(2px);
        opacity: 0;
        pointer-events: none;
        transition: opacity .16s ease;
      }

      #${LOADER_ID}.is-visible {
        opacity: 1;
        pointer-events: all;
      }

      #${LOADER_ID} .zenaro-cart-refresh-loader__card {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1.25rem 1.6rem;
        border: .1rem solid rgba(150, 0, 231, .14);
        border-radius: 1rem;
        background: #fff;
        color: #9600e7;
        box-shadow: 0 .8rem 2.8rem rgba(43, 20, 58, .13);
        font-size: 1.25rem;
        line-height: 1.2;
        font-weight: 600;
      }

      #${LOADER_ID} .zenaro-cart-refresh-loader__card {
        max-width: 34rem;
        box-sizing: border-box;
      }

      #${LOADER_ID} .zenaro-cart-refresh-loader__copy {
        display: flex;
        flex-direction: column;
        gap: .4rem;
        min-width: 0;
      }

      #${LOADER_ID} .zenaro-cart-refresh-loader__detail {
        color: #51465a;
        font-size: 1.15rem;
        line-height: 1.4;
        font-weight: 400;
      }

      #${LOADER_ID} .zenaro-cart-refresh-loader__spinner {
        width: 2.2rem;
        height: 2.2rem;
        flex: 0 0 auto;
        border: .25rem solid rgba(150, 0, 231, .18);
        border-top-color: #9600e7;
        border-radius: 50%;
        animation: zenaro-cart-refresh-spin .7s linear infinite;
      }

      @keyframes zenaro-cart-refresh-spin {
        to { transform: rotate(360deg); }
      }
    `;

    document.head.appendChild(style);
  };

  const showReloadLoader = () => {
    let loader = document.getElementById(LOADER_ID);

    if (!loader) {
      loader = document.createElement('div');
      loader.id = LOADER_ID;
      loader.setAttribute('role', 'status');
      loader.setAttribute('aria-live', 'polite');
      loader.setAttribute('aria-atomic', 'true');
      loader.innerHTML = '<div class="zenaro-cart-refresh-loader__card"><span class="zenaro-cart-refresh-loader__spinner" aria-hidden="true"></span><span class="zenaro-cart-refresh-loader__copy"><span class="zenaro-cart-refresh-loader__title"></span><span class="zenaro-cart-refresh-loader__detail"></span></span></div>';
      const copy = document.querySelector('cart-drawer')?.dataset;
      loader.querySelector('.zenaro-cart-refresh-loader__title').textContent = copy?.zenaroUpdatingTitle || 'Updating your cart…';
      loader.querySelector('.zenaro-cart-refresh-loader__detail').textContent = copy?.zenaroUpdatingDetail || 'You can choose how to pay in a moment.';
      document.body.appendChild(loader);
    }

    loader.style.removeProperty('pointer-events');
    document.documentElement.classList.add(RELOAD_CLASS);
    requestAnimationFrame(() => loader.classList.add('is-visible'));
  };

  const getCartRequestUrl = (input) => {
    if (typeof input === 'string') return input;
    if (input && input.url) return input.url;
    return '';
  };

  const isCartAddRequest = (input) => {
    return String(getCartRequestUrl(input)).toLowerCase().includes('/cart/add');
  };

  const isCartMutationRequest = (input) => {
    const url = String(getCartRequestUrl(input)).toLowerCase();
    return (
      url.includes('/cart/add') ||
      url.includes('/cart/change') ||
      url.includes('/cart/update')
    );
  };

  window.__zenaroDeferredCart = true;
  let pendingAdds = 0;
  let openingRequested = false;
  let lastOrigin = null;
  let badgeRevision = 0;
  const rememberOrigin = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target.closest('cart-drawer')) return;
    const button = target.closest('button, input[type="submit"]');
    const form = event.type === 'submit' ? target : button?.closest('form');
    let scope = (button || form)?.closest('article, [class*="__tarjeta"], .card-wrapper, .product, product-info, quick-add-modal');
    if (scope && !scope.querySelector('img')) scope = scope.closest('.shopify-section') || scope;
    if (!scope) return;
    const img = scope.querySelector('.is-active img, .product__media img, img');
    const imageRect = img?.getBoundingClientRect();
    const rect = imageRect && imageRect.top >= 0 && imageRect.bottom <= window.innerHeight ? imageRect : (button || event.submitter || form).getBoundingClientRect();
    lastOrigin = { src: img?.currentSrc || img?.src, x: rect.left + rect.width / 2, y: rect.top + rect.height / 2, time: Date.now() };
  };
  document.addEventListener('pointerdown', rememberOrigin, true);
  document.addEventListener('click', rememberOrigin, true);
  document.addEventListener('submit', rememberOrigin, true);
  const beginAdd = () => {
    pendingAdds += 1;
    if (!lastOrigin || Date.now() - lastOrigin.time >= 2000) rememberOrigin({target: document.activeElement, type: 'click'});
    return lastOrigin && Date.now() - lastOrigin.time < 2000 ? { ...lastOrigin } : null;
  };
  const finishAdd = () => {
    pendingAdds = Math.max(0, pendingAdds - 1);
    if (openingRequested && pendingAdds === 0) forceReloadAndReopen();
  };
  window.__zenaroRequestCartOpen = () => {
    if (openingRequested || reloadScheduled) return;
    openingRequested = true;
    showReloadLoader();
    if (pendingAdds === 0) forceReloadAndReopen();
  };
  const added = (origin) => {
    const revision = ++badgeRevision;
    fetch((window.Shopify?.routes?.root || '/') + '?sections=cart-icon-bubble', { headers: { Accept: 'application/json' } })
      .then(response => response.ok ? response.json() : null)
      .then(sections => {
        if (revision !== badgeRevision || !sections?.['cart-icon-bubble']) return;
        const parsed = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const bubble = document.querySelector('#cart-icon-bubble');
        const content = parsed.querySelector('.shopify-section');
        if (bubble && content) bubble.innerHTML = content.innerHTML;
      }).catch(() => {});
    if (openingRequested || !origin?.src || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const target = document.querySelector('#cart-icon-bubble');
    if (!target || typeof target.animate !== 'function') return;
    const header = target.closest('.shopify-section');
    if (header?.classList.contains('shopify-section-header-hidden')) header.classList.remove('shopify-section-header-hidden');
    const end = target.getBoundingClientRect();
    if (!end.width) return;
    const image = document.createElement('img');
    image.src = origin.src;
    image.className = 'zenaro-product-flight';
    image.alt = '';
    image.setAttribute('aria-hidden', 'true');
    const x = Math.min(window.innerWidth - 36, Math.max(36, origin.x));
    const y = Math.min(window.innerHeight - 36, Math.max(36, origin.y));
    image.style.cssText = 'position:fixed;display:block;width:80px;height:80px;max-width:none;object-fit:contain;border-radius:12px;box-shadow:0 3px 14px #00000014;pointer-events:none;z-index:2147483000;will-change:transform,opacity;';
    image.style.left = (x - 40) + 'px';
    image.style.top = (y - 40) + 'px';
    document.body.appendChild(image);
    const dx = end.left + end.width / 2 - x;
    const dy = Math.max(end.height / 2 + 8, end.top + end.height / 2) - y;
    try {
      const flight = image.animate([
        { transform: 'translate(0,0) scale(1)', opacity: 1 },
        { transform: 'translate(' + dx * 0.6 + 'px,' + (dy * 0.55 - 22) + 'px) scale(.65)', opacity: 0.9, offset: .6 },
        { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.08)', opacity: 0 }
      ], { duration: 950, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'forwards' });
      flight.finished.then(() => {
        image.remove();
        if (target.isConnected) target.animate([{transform:'scale(1)'},{transform:'scale(1.1)'},{transform:'scale(1)'}],{duration:220});
      }, () => image.remove());
      setTimeout(() => image.remove(), 1500);
    } catch (error) { image.remove(); }
  };

  let recoveryTimer = null;

  const restoreCachedReleasit = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return false;

    const checkoutButton = drawer.querySelector('#CartDrawer-Checkout');
    if (
      drawer.classList.contains('is-empty') ||
      !checkoutButton ||
      checkoutButton.disabled
    ) {
      return true;
    }

    if (drawer.querySelector('#rsi_buy_now_button, .releasit-cod-form-cart-button')) {
      return true;
    }

    const cachedReleasit = window.__zenaroReleasitNodeCache;
    if (!cachedReleasit || !cachedReleasit.nativeButton || !cachedReleasit.wrapper) {
      return false;
    }

    const checkoutRow = checkoutButton.closest('.cart__ctas') || checkoutButton;
    checkoutRow.insertAdjacentElement('afterend', cachedReleasit.wrapper);

    document.documentElement.dispatchEvent(
      new CustomEvent('zenaro:releasit-restored')
    );

    return Boolean(
      drawer.querySelector('#rsi_buy_now_button, .releasit-cod-form-cart-button')
    );
  };

  const scheduleReleasitRecovery = () => {
    setTimeout(restoreCachedReleasit, 60);

    if (recoveryTimer) clearTimeout(recoveryTimer);
    recoveryTimer = setTimeout(() => {
      recoveryTimer = null;
      if (!restoreCachedReleasit()) forceReloadAndReopen();
    }, 650);
  };

  const patchFetch = () => {
    if (window[FETCH_PATCH_FLAG] || typeof window.fetch !== 'function') return;

    window[FETCH_PATCH_FLAG] = true;
    const originalFetch = window.fetch;

    window.fetch = function() {
      const args = arguments;
      const isAdd = isCartAddRequest(args[0]);
      const conditionalAdd = args[1]?.headers?.['X-Zenaro-Upsell'] === '1';
      const isMutation = isCartMutationRequest(args[0]);
      const deferred = isAdd && !document.querySelector('cart-drawer.active');
      const origin = deferred ? beginAdd() : null;

      return originalFetch.apply(this, args).then((response) => {
        if (deferred && response && response.ok) added(origin);
        if (isMutation && !deferred && response && response.ok) scheduleReleasitRecovery();
        return response;
      }).finally(() => { if (deferred) finishAdd(); });
    };
  };

  const patchXhr = () => {
    if (window[XHR_PATCH_FLAG] || typeof XMLHttpRequest === 'undefined') return;

    window[XHR_PATCH_FLAG] = true;
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function(method, url) {
      this.__zenaroCartAddRequest = isCartAddRequest(url);
      this.__zenaroCartMutationRequest = isCartMutationRequest(url);
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function() {
      const deferred = this.__zenaroCartAddRequest && !document.querySelector('cart-drawer.active');
      const origin = deferred ? beginAdd() : null;
      if (deferred) this.addEventListener('loadend', finishAdd, { once: true });
      if (this.__zenaroCartAddRequest || this.__zenaroCartMutationRequest) {
        this.addEventListener('load', () => {
          if (this.status < 200 || this.status >= 300) return;
          if (deferred) added(origin);
          if (this.__zenaroCartMutationRequest && !deferred) scheduleReleasitRecovery();
        }, { once: true });
      }

      try { return originalSend.apply(this, arguments); } catch (error) {
        if (deferred) { this.removeEventListener("loadend", finishAdd); finishAdd(); }
        throw error;
      }
    };
  };

  const forceReloadAndReopen = () => {
    if (reloadScheduled) return;
    reloadScheduled = true;
    window.__zenaroCartReloadPending = true;

    try {
      sessionStorage.setItem(REOPEN_KEY, 'true');
      sessionStorage.setItem(BOOTSTRAP_KEY, 'true');
    } catch (error) {}

    showReloadLoader();
    setTimeout(() => window.location.reload(), 220);
  };

  window.addEventListener('zenaro:cart-resync', () => {
    if (document.querySelector('cart-drawer.active')) forceReloadAndReopen();
  });

  const getVariantQuantityInCart = async (variantId) => {
    if (!variantId) return null;

    try {
      const response = await fetch(`${routes.cart_url}.js`, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) return null;

      const cartState = await response.json();

      return (cartState.items || []).reduce((quantity, item) => {
        return String(item.variant_id) === String(variantId)
          ? quantity + Number(item.quantity || 0)
          : quantity;
      }, 0);
    } catch (error) {
      return null;
    }
  };

  const responseHasRemainingUpsells = (result) => {
    const drawerHtml = result && result.sections && result.sections['cart-drawer'];
    if (!drawerHtml) return true;

    const documentFragment = new DOMParser().parseFromString(drawerHtml, 'text/html');
    return Boolean(documentFragment.querySelector('.zenaro-cart-accessory-upsells'));
  };

  const setupUpsellAddHandler = () => {
    if (window.__zenaroUpsellAddHandlerBound) return;
    window.__zenaroUpsellAddHandlerBound = true;

    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('.zenaro-cart-accessory-upsells__form form');
      if (!form) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const button = form.querySelector('[type="submit"]');
      const buttonText = button && button.querySelector('span');
      const spinner = button && button.querySelector('.loading__spinner');
      const errorWrapper = form.parentElement && form.parentElement.querySelector('.product-form__error-message-wrapper');
      const errorText = errorWrapper && errorWrapper.querySelector('.product-form__error-message');

      const drawer = form.closest('cart-drawer');
      if (!button || button.disabled || drawer?.dataset.zenaroUpdating === 'true' || window.__zenaroCartReloadPending) return;
      if (drawer) {
        drawer.dataset.zenaroUpdating = 'true';
        drawer.updateRevision = (drawer.updateRevision || 0) + 1;
      }

      const originalText = buttonText ? buttonText.textContent : '+ Añadir';
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      if (buttonText) buttonText.textContent = 'Añadiendo…';
      if (spinner) spinner.classList.remove('hidden');
      if (errorWrapper) errorWrapper.hidden = true;
      if (errorText) errorText.textContent = '';

      let selectedVariantId = null;
      let quantityBefore = null;

      try {
        const formData = new FormData(form);
        const conditionalCard = form.closest('[data-zenaro-upsell-rule]');
        if (conditionalCard) {
          const variantSelect = form.querySelector('[data-zenaro-variant-select]');
          formData.set('id', variantSelect ? variantSelect.value : conditionalCard.dataset.zenaroOfferVariantId);
          formData.set('quantity', '1');
        }
        selectedVariantId = formData.get('id');
        quantityBefore = await getVariantQuantityInCart(selectedVariantId);

        if ((conditionalCard || releasitAlreadyInitialized) && drawer && typeof drawer.getSectionsToRender === 'function') {
          formData.append(
            'sections',
            drawer.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          drawer.setActiveElement(document.activeElement);
        }

        const response = await fetch(routes.cart_add_url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-Zenaro-Upsell': conditionalCard ? '1' : '0'
          },
          body: formData
        });

        let result = {};

        try {
          result = await response.json();
        } catch (parseError) {}

        if (!response.ok) {
          const quantityAfter = await getVariantQuantityInCart(selectedVariantId);
          const productWasAdded =
            quantityBefore !== null &&
            quantityAfter !== null &&
            quantityAfter > quantityBefore;

          if (productWasAdded) {
            if (buttonText) buttonText.textContent = 'Añadido ✓';
            forceReloadAndReopen();
            return;
          }

          throw new Error(result.description || result.message || 'No se pudo añadir el producto.');
        }

        if (buttonText) buttonText.textContent = 'Añadido ✓';
        if (conditionalCard) conditionalCard.closest('zenaro-conditional-upsells')?.rememberNext(conditionalCard);

        if (
          (conditionalCard || releasitAlreadyInitialized) &&
          drawer &&
          result.sections &&
          typeof drawer.renderContents === 'function'
        ) {
          drawer.renderContents(result);
          return;
        }

        forceReloadAndReopen();
        return;
      } catch (error) {
        const quantityAfterError = await getVariantQuantityInCart(selectedVariantId);
        const productWasAddedDespiteError =
          quantityBefore !== null &&
          quantityAfterError !== null &&
          quantityAfterError > quantityBefore;

        if (productWasAddedDespiteError) {
          if (errorWrapper) errorWrapper.hidden = true;
          if (buttonText) buttonText.textContent = 'Añadido ✓';
          forceReloadAndReopen();
          return;
        }

        if (errorText) errorText.textContent = error.message || 'No se pudo añadir el producto.';
        if (errorWrapper) errorWrapper.hidden = false;
        if (buttonText) buttonText.textContent = originalText;
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
      } finally {
        if (drawer) delete drawer.dataset.zenaroUpdating;
      }
    }, true);
  };

  const setupBootstrapReset = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const clearBootstrapIfEmpty = () => {
      if (!drawer.classList.contains('is-empty')) return;

      releasitAlreadyInitialized = false;

      try {
        sessionStorage.removeItem(BOOTSTRAP_KEY);
      } catch (error) {}
    };

    clearBootstrapIfEmpty();

    const observer = new MutationObserver(clearBootstrapIfEmpty);
    observer.observe(drawer, {
      attributes: true,
      attributeFilter: ['class']
    });
  };

  const reopenCartAfterReload = () => {
    let shouldReopen = false;

    try {
      shouldReopen = sessionStorage.getItem(REOPEN_KEY) === 'true';
      sessionStorage.removeItem(REOPEN_KEY);

      if (shouldReopen) {
        sessionStorage.setItem(BOOTSTRAP_KEY, 'true');
        releasitAlreadyInitialized = true;
      }
    } catch (error) {}

    if (!shouldReopen) {
      document.documentElement.classList.remove(RELOAD_CLASS);
      return;
    }
    showReloadLoader();

    let attempts = 0;
    const openCart = () => {
      attempts += 1;
      const drawer = document.querySelector('cart-drawer');

      const native = drawer?.querySelector('#rsi_buy_now_button, .releasit-cod-form-cart-button');
      const ready = drawer?.classList.contains('is-empty') || (native && !native.disabled && native.getBoundingClientRect().height > 0);
      if (!ready && attempts < 40) {
        setTimeout(openCart, 150);
        return;
      }

      document.documentElement.classList.remove(RELOAD_CLASS);
      document.getElementById(LOADER_ID)?.classList.remove('is-visible');
      window.__zenaroCartReloadPending = false;
      if (!drawer) return;
      if (!ready && !drawer.querySelector('[data-zenaro-cod-retry]')) {
        const notice = document.createElement('div');
        notice.dataset.zenaroCodRetry = '';
        notice.setAttribute('role', 'status');
        notice.textContent = 'El pago contra entrega no ha terminado de cargar. ';
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.textContent = 'Reintentar contra entrega';
        retry.addEventListener('click', forceReloadAndReopen);
        notice.appendChild(retry);
        drawer.querySelector('.cart__ctas')?.insertAdjacentElement('afterend', notice);
      }
      if (typeof drawer.open === 'function') {
        drawer.open();
      } else {
        drawer.classList.add('active');
        drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('overflow-hidden');
      }
    };

    setTimeout(openCart, 350);
  };

  installReloadStyles();
  patchFetch();
  patchXhr();
  setupUpsellAddHandler();
  setupBootstrapReset();
  let checkoutNavigationStarted = false;
  const rememberCheckoutNavigation = () => {
    checkoutNavigationStarted = true;
    try { sessionStorage.setItem(CHECKOUT_RETURN_KEY, 'true'); } catch (error) {}
  };
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || !event.target.closest?.('#CartDrawer-Checkout')) return;
    rememberCheckoutNavigation();
  });
  document.addEventListener('submit', (event) => {
    if (event.defaultPrevented || event.submitter?.id !== 'CartDrawer-Checkout') return;
    rememberCheckoutNavigation();
  });
  window.addEventListener('pageshow', (event) => {
    const historyReturn = event.persisted || window.performance?.getEntriesByType('navigation')[0]?.type === 'back_forward';
    if (!historyReturn) return;
    reloadScheduled = false;
    window.__zenaroCartReloadPending = false;
    let needsFreshCart = checkoutNavigationStarted;
    try {
      needsFreshCart ||= sessionStorage.getItem(CHECKOUT_RETURN_KEY) === 'true';
      sessionStorage.removeItem(CHECKOUT_RETURN_KEY);
    } catch (error) {}
    checkoutNavigationStarted = false;
    if (needsFreshCart) {
      forceReloadAndReopen();
      return;
    }
    document.documentElement.classList.remove(RELOAD_CLASS);
    document.getElementById(LOADER_ID)?.classList.remove('is-visible');
    restoreCachedReleasit();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reopenCartAfterReload, { once: true });
  } else {
    reopenCartAfterReload();
  }
})();

